import React, { useState } from 'react';
import axios from 'axios';
import { Bell, CheckCircle2, Loader2, Mail, MessageCircle, Moon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RASHIS = [
  { idx: 0, hi: 'मेष', en: 'Aries', symbol: '♈' },
  { idx: 1, hi: 'वृषभ', en: 'Taurus', symbol: '♉' },
  { idx: 2, hi: 'मिथुन', en: 'Gemini', symbol: '♊' },
  { idx: 3, hi: 'कर्क', en: 'Cancer', symbol: '♋' },
  { idx: 4, hi: 'सिंह', en: 'Leo', symbol: '♌' },
  { idx: 5, hi: 'कन्या', en: 'Virgo', symbol: '♍' },
  { idx: 6, hi: 'तुला', en: 'Libra', symbol: '♎' },
  { idx: 7, hi: 'वृश्चिक', en: 'Scorpio', symbol: '♏' },
  { idx: 8, hi: 'धनु', en: 'Sagittarius', symbol: '♐' },
  { idx: 9, hi: 'मकर', en: 'Capricorn', symbol: '♑' },
  { idx: 10, hi: 'कुंभ', en: 'Aquarius', symbol: '♒' },
  { idx: 11, hi: 'मीन', en: 'Pisces', symbol: '♓' },
];

export default function RashifalSubscribe({ defaultRashiIdx = null, lang = 'hi' }) {
  const [rashiIdx, setRashiIdx] = useState(defaultRashiIdx);
  const [whatsapp, setWhatsapp] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const labels = {
    hi: {
      title: 'Daily Rashifal — Free Subscription',
      sub: 'हर सुबह 7 बजे (IST) WhatsApp / Email पर पाएं — आज का Parashari फलादेश, चंद्राष्टम alert, घरेलू उपाय व श्लोक।',
      pickRashi: 'अपनी राशि चुनें',
      whatsappLabel: 'WhatsApp Number (+91...)',
      emailLabel: 'Email Address (optional)',
      submit: 'Daily Rashifal Subscribe करें',
      success: '✅ Subscription चालू हो गई! कल सुबह 7 बजे (IST) से रोज़ाना मिलेगा।',
      atleastOne: 'WhatsApp या Email — कम से कम एक देना आवश्यक है।',
      selectRashi: 'पहले अपनी राशि चुनें।',
      free: '100% FREE · WhatsApp + Email',
      unsubAnytime: 'कभी भी एक click पर unsubscribe कर सकते हैं।',
    },
    en: {
      title: 'Daily Rashifal — Free Subscription',
      sub: 'Receive every morning at 7 AM IST on WhatsApp / Email — today\'s Parashari prediction, Chandrashtama alerts, home remedies & shlokas.',
      pickRashi: 'Select your rashi',
      whatsappLabel: 'WhatsApp Number (with country code)',
      emailLabel: 'Email Address (optional)',
      submit: 'Subscribe to Daily Rashifal',
      success: '✅ Subscription activated! You\'ll receive it daily 7 AM IST starting tomorrow.',
      atleastOne: 'Provide at least one — WhatsApp or Email.',
      selectRashi: 'Please pick your rashi first.',
      free: '100% FREE · WhatsApp + Email',
      unsubAnytime: 'Unsubscribe anytime with one click.',
    },
  };
  const t = labels[lang] || labels.hi;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (rashiIdx === null || rashiIdx === undefined) {
      setError(t.selectRashi);
      return;
    }
    if (!whatsapp && !email) {
      setError(t.atleastOne);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/rashifal/subscribe`, {
        rashi_idx: rashiIdx,
        whatsapp: whatsapp || null,
        email: email || null,
        lang,
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-gradient-to-br from-emerald-900/30 via-teal-900/20 to-gold/10 border-2 border-emerald-400/40 rounded-2xl p-6 sm:p-8 text-center" data-testid="rashifal-subscribe-success">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
        <h3 className="font-heading text-2xl text-text-main mb-2">
          {success.rashi?.name_hi} ({success.rashi?.name_en})
        </h3>
        <p className="text-emerald-300 mb-3">{t.success}</p>
        <div className="flex justify-center gap-3 text-xs text-text-muted">
          {success.channels?.whatsapp && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-500/15 border border-green-400/30 text-green-300">
              <MessageCircle className="w-3 h-3" /> WhatsApp ✓
            </span>
          )}
          {success.channels?.email && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 text-blue-300">
              <Mail className="w-3 h-3" /> Email ✓
            </span>
          )}
        </div>
        <p className="text-xs text-text-muted/70 mt-4">{t.unsubAnytime}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-900/40 via-indigo-900/25 to-gold/10 border-2 border-gold/30 rounded-2xl p-6 sm:p-8" data-testid="rashifal-subscribe-form">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full bg-gold/20 flex items-center justify-center">
          <Bell className="w-5 h-5 text-gold" />
        </div>
        <div>
          <h3 className="font-heading text-xl sm:text-2xl text-text-main">{t.title}</h3>
          <span className="text-[10px] text-emerald-300 uppercase tracking-widest font-semibold">{t.free}</span>
        </div>
      </div>
      <p className="text-sm text-text-muted leading-relaxed mb-5">{t.sub}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rashi picker */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-gold mb-2 font-semibold">
            <Moon className="w-3 h-3 inline mr-1" /> {t.pickRashi}
          </label>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
            {RASHIS.map((r) => (
              <button
                key={r.idx}
                type="button"
                onClick={() => setRashiIdx(r.idx)}
                data-testid={`subscribe-rashi-${r.en.toLowerCase()}`}
                className={`flex flex-col items-center justify-center gap-0.5 px-2 py-2.5 rounded-xl border transition-all text-xs ${
                  rashiIdx === r.idx
                    ? 'bg-gold/20 border-gold text-gold font-semibold scale-[1.02]'
                    : 'bg-white/5 border-white/10 text-text-muted hover:border-gold/30'
                }`}
              >
                <span className="text-lg">{r.symbol}</span>
                <span className="leading-none">{lang === 'hi' ? r.hi : r.en}</span>
              </button>
            ))}
          </div>
        </div>

        {/* WhatsApp */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-text-muted mb-1.5 font-medium">
            <MessageCircle className="w-3 h-3 inline mr-1 text-green-400" /> {t.whatsappLabel}
          </label>
          <input
            type="tel"
            placeholder="+91 93113 69729"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            data-testid="subscribe-whatsapp-input"
            className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-text-main placeholder:text-text-muted/50 focus:border-gold/50 focus:outline-none"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs uppercase tracking-wider text-text-muted mb-1.5 font-medium">
            <Mail className="w-3 h-3 inline mr-1 text-blue-400" /> {t.emailLabel}
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="subscribe-email-input"
            className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 text-text-main placeholder:text-text-muted/50 focus:border-gold/50 focus:outline-none"
          />
        </div>

        {error && (
          <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="subscribe-error">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="subscribe-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gold text-bg-primary font-semibold hover:bg-yellow-400 disabled:opacity-60 transition"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing…</> : <><Bell className="w-4 h-4" /> {t.submit}</>}
        </button>

        <p className="text-[11px] text-text-muted/70 text-center">{t.unsubAnytime}</p>
      </form>
    </div>
  );
}
